/**
 * Pending Toast Utility
 * Queue toast notifications across navigation boundaries via sessionStorage.
 * Toasts queued before navigation are consumed and displayed after the route change.
 *
 * Usage:
 *   queueToast('success', 'Account created!');     // before navigate()
 *   consumeToasts().forEach(t => showToast[t.type](t.message));  // in ToastHandler
 */

type ToastType = 'success' | 'info' | 'warning' | 'error';

interface PendingToast {
  type: ToastType;
  message: string;
}

const STORAGE_KEY = 'pendingToasts';

/** Queue a toast to be shown after the next navigation. */
export function queueToast(type: ToastType, message: string): void {
  const existing: PendingToast[] = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
  existing.push({ type, message });
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
}

/** Consume all queued toasts (reads and clears). */
export function consumeToasts(): PendingToast[] {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  sessionStorage.removeItem(STORAGE_KEY);
  try {
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

/** Clear all queued toasts without consuming. */
export function clearToasts(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}
